import { Target, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useApp } from '../context/AppContext';

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const ProfitLossPage = () => {
  const { kpiData, revenueList, expenseList, loading } = useApp();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  const totalRevenue = revenueList.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalExpenses = expenseList.reduce((sum, item) => sum + (item.amount || 0), 0);
  const netProfit = totalRevenue - totalExpenses;
  const profitMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(1) : 0;

  // Group expenses by category
  const expensesByCategory = expenseList.reduce((acc, item) => {
    const cat = item.expense_category || 'Other';
    acc[cat] = (acc[cat] || 0) + (item.amount || 0);
    return acc;
  }, {});

  const expenseCategoryData = Object.entries(expensesByCategory).map(([category, amount]) => ({
    category,
    amount
  })).sort((a, b) => b.amount - a.amount);

  // Monthly P&L data
  const monthlyData = {};
  revenueList.forEach(item => {
    const month = item.date?.slice(0, 7);
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, expenses: 0 };
      monthlyData[month].revenue += item.amount || 0;
    }
  });
  expenseList.forEach(item => {
    const month = item.date?.slice(0, 7);
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, expenses: 0 };
      monthlyData[month].expenses += item.amount || 0;
    }
  });

  const pnlChartData = Object.values(monthlyData)
    .map(item => ({
      ...item,
      profit: item.revenue - item.expenses
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Profit & Loss</h1>
        <p className="text-gray-500 text-sm">Financial performance summary</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <span className="text-gray-500 text-sm">Total Revenue</span>
          </div>
          <p className="text-xl font-bold text-gray-800">{formatNumber(totalRevenue)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-red-500">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-5 h-5 text-red-600" />
            <span className="text-gray-500 text-sm">Total Expenses</span>
          </div>
          <p className="text-xl font-bold text-gray-800">{formatNumber(totalExpenses)}</p>
        </div>
        <div className={`bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 RM{netProfit >= 0 ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className={`w-5 h-5 RM{netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            <span className="text-gray-500 text-sm">Net Profit</span>
          </div>
          <p className={`text-xl font-bold RM{netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatNumber(netProfit)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-blue-600" />
            <span className="text-gray-500 text-sm">Profit Margin</span>
          </div>
          <p className={`text-xl font-bold RM{parseFloat(profitMargin) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{profitMargin}%</p>
        </div>
      </div>

      {/* P&L Chart */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-medium mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-red-700" /> Monthly P&L Trend
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={pnlChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10 }} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
            <Tooltip 
              formatter={(value) => formatNumber(value)}
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} 
            />
            <Bar dataKey="revenue" fill="#10B981" name="Revenue" />
            <Bar dataKey="expenses" fill="#EF4444" name="Expenses" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profit Trend */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4">Profit Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={pnlChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
              <Tooltip 
                formatter={(value) => formatNumber(value)}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} 
              />
              <Line type="monotone" dataKey="profit" stroke="#C41E3A" strokeWidth={2} dot={{ fill: '#C41E3A' }} name="Net Profit" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Expense Breakdown */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4">Expense Breakdown</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={expenseCategoryData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} />
              <YAxis dataKey="category" type="category" tick={{ fill: '#6b7280', fontSize: 10 }} width={80} />
              <Tooltip 
                formatter={(value) => formatNumber(value)}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} 
              />
              <Bar dataKey="amount" fill="#C41E3A" radius={[0, 4, 4, 0]} name="Amount" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* P&L Statement */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-red-700 to-red-800">
          <h3 className="font-semibold text-white">Profit & Loss Statement</h3>
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-3 font-medium text-gray-800">Revenue</td>
                <td className="py-3 text-right text-emerald-600 font-bold">{formatNumber(totalRevenue)}</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 font-medium text-gray-800">Less: Total Expenses</td>
                <td className="py-3 text-right text-red-600 font-bold">({formatNumber(totalExpenses)})</td>
              </tr>
              {expenseCategoryData.map((item, idx) => (
                <tr key={idx} className="border-b border-gray-50">
                  <td className="py-2 pl-6 text-gray-600">- {item.category}</td>
                  <td className="py-2 text-right text-gray-600">{formatNumber(item.amount)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50">
                <td className="py-4 font-bold text-gray-800">Net Profit / (Loss)</td>
                <td className={`py-4 text-right font-bold text-lg RM{netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatNumber(netProfit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossPage;
